import { Link } from "react-router-dom";
import {
  ArrowUpRight, Search, CreditCard, Wand2, Palette, Smartphone,
  MailPlus, LineChart, ShieldCheck, LifeBuoy, Languages, Sparkles,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL, serviceSchema } from "@/lib/seo";
import { HOME } from "@/constants/testIds";

const items = [
  {
    icon: Palette,
    t: "Tailored Design",
    d: "Never templated. Each project begins on a blank page, with typography, palette, photography and voice considered as one quiet whole.",
  },
  {
    icon: Sparkles,
    t: "Luxury-Inspired Aesthetics",
    d: "Editorial typography, quiet luxury, and the kind of considered space usually reserved for flagship brands.",
  },
  {
    icon: Smartphone,
    t: "Mobile-First Development",
    d: "Built for the device in your customer&rsquo;s hand. Fast, light, and designed to pass Core Web Vitals on launch day.",
  },
  {
    icon: Search,
    t: "SEO Foundations & Growth",
    d: "Schema, semantic structure, meta and OpenGraph done properly from day one. Ongoing on-page SEO so you climb steadily, not just launch loudly.",
  },
  {
    icon: CreditCard,
    t: "Stripe-Integrated Payments",
    d: "Take card payments, deposits, bookings, gift cards, or recurring subscriptions natively through Stripe. UK-ready, secure, and beautifully integrated into the site.",
  },
  {
    icon: Wand2,
    t: "Custom Functionality",
    d: "Booking systems, member areas, calculators, quote builders, gated content, automated lead capture. Bespoke features built around how your business actually works.",
  },
  {
    icon: MailPlus,
    t: "Strategic Lead Capture",
    d: "Refined enquiry flows that respect a visitor&rsquo;s time. Multi-step forms, instant notifications, and CRM-ready exports of every lead.",
  },
  {
    icon: LineChart,
    t: "Analytics & Insight",
    d: "Cookie-respectful analytics set up correctly, with monthly notes from us highlighting what is working and what to refine.",
  },
  {
    icon: ShieldCheck,
    t: "Hosting, Security & Backups",
    d: "Premium hosting, SSL, automated daily backups, uptime monitoring and CDN delivery, included in your monthly support.",
  },
  {
    icon: LifeBuoy,
    t: "Ongoing Studio Support",
    d: "Direct line to the atelier for refinements, content swaps, and small new features. Not a launch-and-leave studio.",
  },
  {
    icon: Languages,
    t: "Copy & Content Polish",
    d: "Brand-led copy refinement included, so your words sound as considered as the design that carries them.",
  },
  {
    icon: Sparkles,
    t: "A Quiet Boutique Experience",
    d: "Every project is treated like the only one on the desk. Calm correspondence, considered drafts, and a studio that stays with you long after launch.",
  },
];

export default function Services() {
  const crumbs = [
    { name: "Home", url: `${SITE_URL}/` },
    { name: "Services", url: `${SITE_URL}/services` },
  ];
  const serviceList = items.map((it) => ({ name: it.t, description: it.d }));
  return (
    <main className="pt-32 sm:pt-40 bg-lak-bg">
      <Seo
        title="Web Design Services · SEO, Stripe, Custom Functionality"
        description="Twelve thoughtful capabilities from LAK Atelier: bespoke design, SEO foundations, Stripe-integrated payments, custom functionality, hosting and ongoing support."
        keywords="web design services UK, boutique web design, SEO web design, Stripe website integration, custom website functionality, mobile-first web design"
        breadcrumbs={crumbs}
        schemas={[serviceSchema(serviceList)]}
      />
      <section className="lak-section pb-20 sm:pb-32">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
          <Breadcrumbs items={crumbs} className="mb-8" />
          <Reveal><span className="eyebrow">Services, Why LAK Atelier</span></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-8 font-serif text-6xl sm:text-7xl lg:text-9xl font-light tracking-tight leading-[0.96] max-w-5xl">
              Everything your brand needs, <em className="italic text-lak-sage">quietly handled.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-lg text-lak-soft">
              From considered design to Stripe-powered checkouts and bespoke functionality. Twelve thoughtful capabilities, all in one transparent monthly plan.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid={HOME.whyLakSection} className="lak-section pb-32">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <Reveal key={it.t} delay={i * 0.05}>
                  <div className="bg-lak-bg p-10 lg:p-12 h-full group transition-colors hover:bg-lak-secondary">
                    <div className="flex items-start justify-between">
                      <span className="font-serif italic text-lak-highlight text-3xl">{String(i+1).padStart(2,"0")}</span>
                      <Icon size={22} strokeWidth={1.25} className="text-lak-sage opacity-60 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h3 className="mt-8 font-serif text-2xl lg:text-3xl font-light tracking-tight leading-snug">{it.t}</h3>
                    <p className="mt-5 text-lak-soft leading-relaxed text-[15px]" dangerouslySetInnerHTML={{ __html: it.d }} />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lak-section py-28 bg-lak-secondary">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <Reveal>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                See how it would feel <em className="italic text-lak-sage">for your brand.</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-wrap gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 btn-sage text-[11px] tracking-[0.28em] uppercase">
              Start Your Project <ArrowUpRight size={16} strokeWidth={1.25} />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-4 btn-outline text-[11px] tracking-[0.28em] uppercase">
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
